import React from 'react';
import { adminStats } from '../../data/adminMock';
import { TrendingUp, Users, Phone, Bot, DollarSign, Clock, ArrowUpRight } from 'lucide-react';

const AdminAnalytics = () => {
  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Analytics</h1>
        <p className="text-gray-400">Platform performance and usage statistics</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-400/10 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <span className="flex items-center gap-1 text-green-400 text-sm">
              <ArrowUpRight className="w-4 h-4" /> 12.5%
            </span>
          </div>
          <p className="text-gray-400 text-sm">Total Users</p>
          <p className="text-2xl font-bold text-white">{adminStats.totalUsers.toLocaleString()}</p>
        </div>

        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-teal-400/10 rounded-lg flex items-center justify-center">
              <Bot className="w-6 h-6 text-teal-400" />
            </div>
            <span className="flex items-center gap-1 text-green-400 text-sm">
              <ArrowUpRight className="w-4 h-4" /> 8.2%
            </span>
          </div>
          <p className="text-gray-400 text-sm">Active Agents</p>
          <p className="text-2xl font-bold text-white">{adminStats.activeAgents.toLocaleString()}</p>
        </div>

        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-400/10 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-purple-400" />
            </div>
            <span className="flex items-center gap-1 text-green-400 text-sm">
              <ArrowUpRight className="w-4 h-4" /> 23.5%
            </span>
          </div>
          <p className="text-gray-400 text-sm">Total Calls</p>
          <p className="text-2xl font-bold text-white">{(adminStats.totalCalls / 1000000).toFixed(2)}M</p>
        </div>

        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-400/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <span className="flex items-center gap-1 text-green-400 text-sm">
              <ArrowUpRight className="w-4 h-4" /> 15.3%
            </span>
          </div>
          <p className="text-gray-400 text-sm">Revenue</p>
          <p className="text-2xl font-bold text-white">${(adminStats.revenue / 1000).toFixed(0)}K</p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calls Chart */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">Calls Over Time</h3>
          <div className="h-64 flex items-end justify-between gap-2">
            {[65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88, 92].map((height, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-teal-400/20 rounded-t hover:bg-teal-400/40 transition-colors"
                  style={{ height: `${height}%` }}
                />
                <span className="text-gray-500 text-xs">
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][index]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Users Growth Chart */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-6">User Growth</h3>
          <div className="h-64 flex items-end justify-between gap-2">
            {[30, 35, 45, 50, 58, 65, 72, 78, 85, 90, 95, 100].map((height, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-blue-400/20 rounded-t hover:bg-blue-400/40 transition-colors"
                  style={{ height: `${height}%` }}
                />
                <span className="text-gray-500 text-xs">
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][index]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-orange-400/10 rounded-xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-orange-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Avg Call Duration</p>
              <p className="text-xl font-bold text-white">{adminStats.avgCallDuration}</p>
            </div>
          </div>
        </div>

        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-teal-400/10 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-teal-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Growth Rate</p>
              <p className="text-xl font-bold text-white">{adminStats.growthRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-purple-400/10 rounded-xl flex items-center justify-center">
              <Bot className="w-7 h-7 text-purple-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Agents per User</p>
              <p className="text-xl font-bold text-white">6.8</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
